import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, User, CheckSquare, KeyRound } from "lucide-react";
import { useTheme } from "../context/ThemeContext";


// Yalnızca telefon ve tablet görünümünü sıkılaştırır.
// Masaüstü stilleri, veri akışı ve işlem fonksiyonları değiştirilmez.
function useMobileOnlyResponsiveLayout(scopeClass, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    const bodyClass = `${scopeClass}-active`;
    const styleId = `${scopeClass}-style`;
    document.body.classList.add(bodyClass);

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      const scope = `body.${bodyClass}`;

      style.textContent = `
        @media (max-width: 1023px) {
          ${scope},
          ${scope} #root {
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
          }

          ${scope} main {
            width: 100%;
            min-width: 0;
            max-width: 100vw;
          }

          ${scope} table {
            display: block;
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            overscroll-behavior-inline: contain;
            -webkit-overflow-scrolling: touch;
          }

          ${scope} iframe,
          ${scope} video,
          ${scope} canvas {
            max-width: 100%;
          }

          ${scope} [class*="fixed"][class*="inset-0"] {
            overflow-y: auto;
            overscroll-behavior: contain;
          }
        }

        @media (max-width: 767px) {
          ${scope} {
            font-size: 13px;
            -webkit-text-size-adjust: 100%;
            text-size-adjust: 100%;
          }

          ${scope} *,
          ${scope} *::before,
          ${scope} *::after {
            box-sizing: border-box;
          }

          ${scope} h1,
          ${scope} [class~="text-5xl"],
          ${scope} [class~="text-4xl"] {
            font-size: clamp(1.7rem, 7.5vw, 2rem) !important;
            line-height: 1.12 !important;
            letter-spacing: -0.025em !important;
          }

          ${scope} h2,
          ${scope} [class~="text-3xl"] {
            font-size: clamp(1.35rem, 5.8vw, 1.6rem) !important;
            line-height: 1.18 !important;
          }

          ${scope} h3,
          ${scope} [class~="text-2xl"] {
            font-size: 1.15rem !important;
            line-height: 1.25 !important;
          }

          ${scope} [class~="text-xl"] {
            font-size: 1rem !important;
            line-height: 1.35 !important;
          }

          ${scope} p,
          ${scope} li {
            line-height: 1.55;
          }

          ${scope} [class~="p-10"],
          ${scope} [class~="p-9"],
          ${scope} [class~="p-8"],
          ${scope} [class~="p-7"],
          ${scope} [class~="p-6"],
          ${scope} [class~="p-5"] {
            padding: 1rem !important;
          }

          ${scope} [class~="px-10"],
          ${scope} [class~="px-9"],
          ${scope} [class~="px-8"],
          ${scope} [class~="px-7"],
          ${scope} [class~="px-6"],
          ${scope} [class~="px-5"] {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }

          ${scope} [class~="py-10"],
          ${scope} [class~="py-9"],
          ${scope} [class~="py-8"],
          ${scope} [class~="py-7"],
          ${scope} [class~="py-6"] {
            padding-top: 1rem !important;
            padding-bottom: 1rem !important;
          }

          ${scope} [class~="gap-10"],
          ${scope} [class~="gap-9"],
          ${scope} [class~="gap-8"],
          ${scope} [class~="gap-7"],
          ${scope} [class~="gap-6"],
          ${scope} [class~="gap-5"] {
            gap: 0.8rem !important;
          }

          ${scope} [class~="mt-10"],
          ${scope} [class~="mt-9"],
          ${scope} [class~="mt-8"],
          ${scope} [class~="mt-7"],
          ${scope} [class~="mt-6"] {
            margin-top: 1rem !important;
          }

          ${scope} [class~="mb-10"],
          ${scope} [class~="mb-9"],
          ${scope} [class~="mb-8"],
          ${scope} [class~="mb-7"],
          ${scope} [class~="mb-6"] {
            margin-bottom: 1rem !important;
          }

          ${scope} [class*="grid-cols-2"],
          ${scope} [class*="grid-cols-3"],
          ${scope} [class*="grid-cols-4"],
          ${scope} [class*="grid-cols-5"],
          ${scope} [class*="grid-cols-6"],
          ${scope} [class*="grid-cols-["] {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          ${scope} form [class~="flex"] {
            flex-wrap: wrap;
          }

          ${scope} button,
          ${scope} [role="button"] {
            max-width: 100%;
            min-height: 38px;
            white-space: normal;
            touch-action: manipulation;
          }

          ${scope} input,
          ${scope} select,
          ${scope} textarea {
            width: 100%;
            max-width: 100%;
            min-height: 42px;
            font-size: 16px !important;
          }

          ${scope} textarea {
            min-height: 96px;
          }

          ${scope} table {
            font-size: 11px;
          }

          ${scope} th,
          ${scope} td {
            padding: 0.55rem 0.65rem !important;
            white-space: nowrap;
          }

          ${scope} svg[class*="h-["],
          ${scope} canvas[class*="h-["] {
            height: 230px !important;
            max-height: 42vh;
          }

          ${scope} iframe {
            min-height: 230px !important;
            height: 48vh !important;
          }

          ${scope} video {
            max-height: 52vh;
          }

          ${scope} img {
            max-width: 100%;
          }

          ${scope} [class*="fixed"][class*="inset-0"] {
            align-items: flex-start !important;
            padding: max(0.75rem, env(safe-area-inset-top)) 0.75rem max(0.75rem, env(safe-area-inset-bottom)) !important;
          }

          ${scope} [class*="fixed"][class*="inset-0"] > [class*="max-w-"] {
            width: 100% !important;
            max-width: 100% !important;
            max-height: calc(100dvh - 1.5rem) !important;
            margin: auto !important;
            overflow-y: auto !important;
          }

          ${scope} [class*="rounded-[2"],
          ${scope} [class*="rounded-[3"] {
            border-radius: 1.1rem !important;
          }

          ${scope} [class*="tracking-[0.3"],
          ${scope} [class*="tracking-[0.4"] {
            letter-spacing: 0.16em !important;
          }

          ${scope} [class*="max-w-7xl"],
          ${scope} [class*="max-w-6xl"],
          ${scope} [class*="max-w-5xl"],
          ${scope} [class*="max-w-4xl"],
          ${scope} [class*="max-w-3xl"] {
            max-width: 100% !important;
          }
        }

        @media (max-width: 380px) {
          ${scope} h1,
          ${scope} [class~="text-5xl"],
          ${scope} [class~="text-4xl"] {
            font-size: 1.55rem !important;
          }

          ${scope} h2,
          ${scope} [class~="text-3xl"] {
            font-size: 1.25rem !important;
          }

          ${scope} button,
          ${scope} [role="button"] {
            font-size: 11px !important;
          }
        }
      `;

      document.head.appendChild(style);
    }

    return () => {
      document.body.classList.remove(bodyClass);
    };
  }, [scopeClass, enabled]);
}
const IS_LOCAL =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const API_BASE = IS_LOCAL ? "https://www.wolfmediaajans.com" : "";
const FETCH_CREDENTIALS = IS_LOCAL ? "omit" : "include";

const API_LOGIN = `${API_BASE}/api/login.php`;
const API_PASSWORD_RESET_REQUEST = `${API_BASE}/api/password-reset-request.php`;
const API_FORCE_PASSWORD_CHANGE = `${API_BASE}/api/force-password-change.php`;
const API_CONSENT_ACCEPT = `${API_BASE}/api/consent-accept.php`;
const API_CONSENT_AUDIT_SAVE = `${API_BASE}/api/consent-audit-save.php`;
const API_AGENCY_TRACKING_SAVE = `${API_BASE}/api/agency-tracking-save.php`;
const CONSENT_VERSION = "v1.0";

const CONSENT_TEXT = `Wolf Media Ajans Kullanıcı Kullanım ve Gizlilik Onayı

Bu platform Wolf Media Ajans’a ait özel bir yönetim ve takip sistemidir. Sistemde yer alan yayıncı bilgileri, yönetici bilgileri, performans verileri, raporlar, eğitim içerikleri, mesajlar, editör destek talepleri, dosyalar, görseller, videolar ve panel ekranları yalnızca yetkili kullanıcıların kullanımı içindir.

Kullanıcı, kendisine verilen kullanıcı adı ve şifre ile sisteme giriş yaptığında hesabının güvenliğinden sorumlu olduğunu kabul eder. Kullanıcı adı, şifre, panel erişimi veya sistemde görünen bilgiler üçüncü kişilerle paylaşılamaz.

Panel ekran görüntüleri, raporlar, yayıncı listeleri, performans verileri, edit talepleri, sistem içi mesajlar ve dosyalar Wolf Media Ajans izni olmadan dışarı aktarılamaz, paylaşılamaz veya farklı amaçlarla kullanılamaz.

Bu sistemde sunulan tüm yazılım altyapısı, ekran tasarımları, rapor yapıları, kullanıcı verileri, içerik yönetimi, editör destek süreçleri ve işleyiş modeli Wolf Media Ajans’a aittir. Kullanıcı, sisteme yalnızca kendisine verilen yetki kapsamında erişebilir ve yetkisi dışında işlem yapamaz.

Kullanıcı, sistem üzerinde yaptığı işlemlerin güvenlik ve takip amacıyla kayıt altına alınabileceğini kabul eder. Bu kapsamda giriş, şifre değişikliği, kullanıcı onayı, rol, durum, içerik, mesaj, talep ve güncelleme işlemleri sistem kayıtlarında saklanabilir.

Bu onayı kabul eden kullanıcı, Wolf Media Ajans platformunu gizlilik kurallarına uygun şekilde kullanacağını, kendisine ait olmayan bilgileri paylaşmayacağını ve sistemdeki içerikleri yalnızca görev/yetki kapsamı içinde kullanacağını beyan eder.`;

function getRole(user) {
  return String(user?.role || "").toLowerCase().trim();
}

function mustChangePassword(user) {
  return (
    user?.mustChangePassword === true ||
    user?.must_change_password === 1 ||
    user?.must_change_password === "1"
  );
}

function needsConsent(user) {
  return (
    user?.requireConsent === true ||
    user?.require_consent === 1 ||
    user?.require_consent === "1"
  );
}

function normalizeAgencyTrackingStatus(value) {
  const text = String(value || "Aktif").toLowerCase().trim();
  return text.includes("pasif") || text.includes("passive") || text.includes("inactive") ? "Pasif" : "Aktif";
}

function needsAgencyTrackingConsent(user) {
  const role = getRole(user);
  if (role !== "publisher") return false;
  return !(
    user?.agencyTrackingChoiceAt ||
    user?.agency_tracking_choice_at ||
    user?.agencyTrackingConsentAt ||
    user?.agency_tracking_consent_at
  );
}

function navigateByRole(navigate, user) {
  const role = getRole(user);
  const username = String(user?.username || "").toLowerCase().trim();

  if (role === "master_admin" || role === "admin") {
    navigate("/admin-dashboard", { replace: true });
  } else if (role === "edit_admin" || role === "editor" || username === "edit" || username === "editor") {
    navigate("/edit-admin", { replace: true });
  } else if (role === "scout" || role === "scout_manager") {
    navigate("/scout-center", { replace: true });
  } else if (role === "employee" || role === "manager") {
    navigate("/employee-dashboard", { replace: true });
  } else {
    navigate("/publisher-dashboard", { replace: true });
  }
}

function ConsentModal({ user, scrolled, accepting, error, onScroll, onAccept }) {
  if (!user) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 px-4">
      <div className="w-full max-w-3xl rounded-[2rem] border border-yellow-500/25 bg-[#0d0d0d] p-7 shadow-[0_24px_90px_rgba(0,0,0,0.75)]">
        <div className="mb-5">
          <div className="text-xs font-bold uppercase tracking-[0.32em] text-yellow-300">
            Zorunlu Kullanıcı Onayı
          </div>
          <h2 className="mt-2 text-2xl font-black text-white">
            Wolf Media Ajans Kullanım ve Gizlilik Onayı
          </h2>
          <p className="mt-2 text-sm leading-6 text-white/50">
            Şifre değişikliği sonrası sisteme devam edebilmek için metni aşağı kaydırıp onaylamanız gerekir.
          </p>
        </div>

        <div
          onScroll={onScroll}
          className="max-h-[390px] overflow-y-auto whitespace-pre-line rounded-2xl border border-white/10 bg-black/45 p-5 text-sm leading-7 text-white/72 [scrollbar-color:rgba(234,179,8,.75)_rgba(255,255,255,.06)] [scrollbar-width:thin]"
        >
          {CONSENT_TEXT}
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-200">
            {error}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs leading-5 text-white/45">
            Kullanıcı: <span className="font-bold text-white/75">{user.username}</span> • Onay versiyonu: {CONSENT_VERSION}
          </div>

          <button
            type="button"
            disabled={!scrolled || accepting}
            onClick={onAccept}
            className="rounded-2xl bg-yellow-500 px-6 py-3 text-sm font-black text-black transition hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-45"
          >
            {accepting ? "Kaydediliyor..." : scrolled ? "Okudum, Kabul Ediyorum" : "Metni aşağı kaydırın"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgencyTrackingModal({ user, saving, error, onSelect }) {
  if (!user) return null;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/88 px-4">
      <div className="w-full max-w-4xl rounded-[2rem] border border-yellow-500/25 bg-[#0d0d0d] p-7 shadow-[0_24px_90px_rgba(0,0,0,0.75)]">
        <div className="mb-5">
          <div className="text-xs font-bold uppercase tracking-[0.32em] text-yellow-300">
            3. Adım · Ajans Takip Tercihi
          </div>
          <h2 className="mt-2 text-2xl font-black text-white">
            Ajansın sizi nasıl takip etmesini istersiniz?
          </h2>
          <p className="mt-2 text-sm leading-6 text-white/50">
            Bu seçim, ajansın size özel performans bilgilendirmesi, yarışma/kampanya duyurusu ve gelişim yönlendirmesi yapıp yapmayacağını belirler. Daha sonra yayıncı panelinden değiştirebilirsiniz.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => onSelect("Aktif")}
            className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-left transition hover:bg-emerald-500/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="text-lg font-black text-emerald-200">Ajans Takibi Aktif</div>
            <p className="mt-3 text-sm leading-6 text-white/65">
              Ajans beni takip etsin; performansımla ilgili bilgilendirme yapsın, yarışmalara/duyurulara dahil etsin ve gelişim yönlendirmeleri sunsun.
            </p>
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={() => onSelect("Pasif")}
            className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-5 text-left transition hover:bg-yellow-500/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="text-lg font-black text-yellow-200">Ajans Takibi Pasif</div>
            <p className="mt-3 text-sm leading-6 text-white/65">
              Verilerimi bu panelden kendim takip etmek istiyorum. Ben talep etmedikçe ajans tarafından özel takip ve yönlendirme yapılmasını istemiyorum.
            </p>
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-200">
            {error}
          </div>
        )}

        <div className="mt-5 rounded-2xl border border-white/10 bg-black/35 p-4 text-xs leading-5 text-white/45">
          Pasif seçen yayıncılar otomatik kampanya ve hediye hesaplamasına dahil edilmez; Master Admin gerekli görürse manuel dahil edebilir.
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  useMobileOnlyResponsiveLayout("wm-login-page-mobile");
  const { theme, isDark } = useTheme();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const [forcePasswordUser, setForcePasswordUser] = useState(null);
  const [forcePasswordForm, setForcePasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [forcePasswordSaving, setForcePasswordSaving] = useState(false);
  const [forcePasswordError, setForcePasswordError] = useState("");

  const [consentUser, setConsentUser] = useState(null);
  const [consentScrolled, setConsentScrolled] = useState(false);
  const [consentAccepting, setConsentAccepting] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [agencyTrackingUser, setAgencyTrackingUser] = useState(null);
  const [agencyTrackingSaving, setAgencyTrackingSaving] = useState(false);
  const [agencyTrackingError, setAgencyTrackingError] = useState("");

  const navigate = useNavigate();

  function continueAfterLogin(user) {
    if (needsConsent(user)) {
      setConsentUser(user);
      setConsentScrolled(false);
      setConsentError("");
      return;
    }

    if (needsAgencyTrackingConsent(user)) {
      setAgencyTrackingUser(user);
      setAgencyTrackingError("");
      return;
    }

    localStorage.setItem("user", JSON.stringify(user));
    navigateByRole(navigate, user);
  }

  async function handleLogin(e) {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      alert("Kullanıcı adı ve şifre zorunlu.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(API_LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: FETCH_CREDENTIALS,
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim(),
        }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.message || "Giriş yapılamadı.");
        return;
      }

      const user = data.user;

      if (mustChangePassword(user)) {
        setForcePasswordUser(user);
        setForcePasswordError("");
        setForcePasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        return;
      }

      continueAfterLogin(user);
    } catch (error) {
      console.error("LOGIN HATA:", error);
      alert("Sunucu bağlantı hatası.");
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPasswordSubmit(e) {
    e.preventDefault();

    if (!forgotUsername.trim() || !forgotPhone.trim()) {
      alert("Kullanıcı adı ve telefon numarası zorunlu.");
      return;
    }

    setForgotLoading(true);

    try {
      const res = await fetch(API_PASSWORD_RESET_REQUEST, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: FETCH_CREDENTIALS,
        body: JSON.stringify({
          username: forgotUsername.trim(),
          phone: forgotPhone.trim(),
        }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(data.message || "Şifre talebi oluşturulamadı.");
        return;
      }

      alert("Şifre talebiniz alınmıştır. Şifreniz yöneticiniz tarafından iletilecektir.");

      setForgotUsername("");
      setForgotPhone("");
      setShowForgotPassword(false);
    } catch (error) {
      console.error("ŞİFRE TALEBİ HATA:", error);
      alert("Sunucu bağlantı hatası.");
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleForcePasswordChange(e) {
    e.preventDefault();

    if (!forcePasswordUser?.id) {
      setForcePasswordError("Kullanıcı bilgisi bulunamadı.");
      return;
    }

    if (
      !forcePasswordForm.currentPassword.trim() ||
      !forcePasswordForm.newPassword.trim() ||
      !forcePasswordForm.confirmPassword.trim()
    ) {
      setForcePasswordError("Mevcut şifre, yeni şifre ve şifre tekrarı zorunludur.");
      return;
    }

    if (forcePasswordForm.newPassword !== forcePasswordForm.confirmPassword) {
      setForcePasswordError("Yeni şifreler eşleşmiyor.");
      return;
    }

    if (forcePasswordForm.newPassword.length < 6) {
      setForcePasswordError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }

    if (forcePasswordForm.currentPassword === forcePasswordForm.newPassword) {
      setForcePasswordError("Yeni şifre mevcut şifre ile aynı olamaz.");
      return;
    }

    setForcePasswordSaving(true);

    try {
      const res = await fetch(API_FORCE_PASSWORD_CHANGE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: FETCH_CREDENTIALS,
        body: JSON.stringify({
          userId: forcePasswordUser.id,
          currentPassword: forcePasswordForm.currentPassword.trim(),
          newPassword: forcePasswordForm.newPassword.trim(),
          confirmPassword: forcePasswordForm.confirmPassword.trim(),
        }),
      });

      const data = await res.json();

      if (!data.success) {
        setForcePasswordError(data.message || "Şifre güncellenemedi.");
        return;
      }

      const updatedUser = {
        ...(data.user || forcePasswordUser),
        mustChangePassword: false,
        must_change_password: 0,
        requireConsent: true,
        require_consent: 1,
      };

      setForcePasswordUser(null);
      setConsentUser(updatedUser);
      setConsentScrolled(false);
      setConsentError("");
    } catch (error) {
      console.error("ZORUNLU ŞİFRE DEĞİŞİM HATA:", error);
      setForcePasswordError("Sunucu bağlantı hatası.");
    } finally {
      setForcePasswordSaving(false);
    }
  }

  function handleConsentScroll(event) {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 14) {
      setConsentScrolled(true);
    }
  }

  async function handleAcceptConsent() {
    if (!consentUser?.id) {
      setConsentError("Kullanıcı bilgisi bulunamadı.");
      return;
    }

    try {
      setConsentAccepting(true);
      setConsentError("");

      const res = await fetch(API_CONSENT_ACCEPT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: FETCH_CREDENTIALS,
        body: JSON.stringify({
          userId: consentUser.id,
          consentVersion: CONSENT_VERSION,
          consentText: CONSENT_TEXT,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        setConsentError(data.message || "Onay kaydedilemedi.");
        return;
      }

      const acceptedAt = data.acceptedAt || new Date().toISOString();
      const acceptedUser = {
        ...consentUser,
        requireConsent: false,
        require_consent: 0,
        consentAcceptedAt: acceptedAt,
        consent_accepted_at: acceptedAt,
        consentVersion: CONSENT_VERSION,
        consent_version: CONSENT_VERSION,
      };

      try {
        await fetch(API_CONSENT_AUDIT_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: FETCH_CREDENTIALS,
          body: JSON.stringify({
            userId: acceptedUser.id,
            username: acceptedUser.username,
            fullName: acceptedUser.fullName || acceptedUser.fullname || "",
            role: acceptedUser.role || "",
            consentVersion: CONSENT_VERSION,
            consentText: CONSENT_TEXT,
          }),
        });
      } catch (auditError) {
        console.error("ONAY DENETİM KAYDI HATA:", auditError);
      }

      setConsentUser(null);
      continueAfterLogin(acceptedUser);
    } catch (error) {
      console.error("ONAY HATA:", error);
      setConsentError("Sunucu bağlantı hatası.");
    } finally {
      setConsentAccepting(false);
    }
  }


  async function handleAgencyTrackingSelect(status) {
    if (!agencyTrackingUser?.id) {
      setAgencyTrackingError("Kullanıcı bilgisi bulunamadı.");
      return;
    }

    try {
      setAgencyTrackingSaving(true);
      setAgencyTrackingError("");
      const normalized = normalizeAgencyTrackingStatus(status);

      const res = await fetch(API_AGENCY_TRACKING_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: FETCH_CREDENTIALS,
        body: JSON.stringify({
          userId: agencyTrackingUser.id,
          username: agencyTrackingUser.username,
          hiddenId: agencyTrackingUser.hiddenId || agencyTrackingUser.hidden_id || agencyTrackingUser.hiddenid,
          status: normalized,
          source: "first_login",
        }),
      });

      const data = await res.json();
      if (!data.success) {
        setAgencyTrackingError(data.message || "Ajans takip tercihi kaydedilemedi.");
        return;
      }

      const updatedUser = {
        ...agencyTrackingUser,
        agencyTrackingStatus: normalized,
        agency_tracking_status: normalized,
        agencyTrackingChoiceAt: data.updatedAt || new Date().toISOString(),
        agency_tracking_choice_at: data.updatedAt || new Date().toISOString(),
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));
      setAgencyTrackingUser(null);
      navigateByRole(navigate, updatedUser);
    } catch (error) {
      console.error("AJANS TAKİP TERCİHİ HATA:", error);
      setAgencyTrackingError("Sunucu bağlantı hatası.");
    } finally {
      setAgencyTrackingSaving(false);
    }
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? "bg-black text-white" : "bg-[#edf2f7] text-slate-900"
    }`}>
      {isDark ? (
        <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.10),transparent_30%),linear-gradient(180deg,#060606_0%,#090909_45%,#000_100%)]" />
      ) : (
        <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(234,179,8,0.12),transparent_40%),linear-gradient(180deg,#f8fafc_0%,#edf2f7_100%)]" />
      )}

      <div className="relative z-10 flex min-h-screen items-start justify-center px-4 pb-6 pt-[max(1rem,env(safe-area-inset-top))] sm:items-center sm:py-8">
        <div className={`w-full max-w-[400px] rounded-[1.45rem] border px-5 py-6 shadow-2xl backdrop-blur-xl transition-colors sm:max-w-[430px] sm:rounded-[2.2rem] sm:px-8 sm:py-10 ${
          isDark
            ? "border-white/10 bg-white/[0.04] text-white shadow-[0_20px_65px_rgba(0,0,0,0.55)]"
            : "border-slate-200 bg-white text-slate-900 shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
        }`}>
          <div className="mb-5 flex flex-col items-center text-center sm:mb-8">
            <img src="/logo192.png" alt="Wolf Media" className="mb-3 h-14 w-14 rounded-2xl object-contain sm:mb-6 sm:h-20 sm:w-20" />

            <h1 className={`text-[2rem] font-black leading-none sm:text-5xl ${isDark ? "text-white" : "text-slate-900"}`}>Giriş</h1>

            <p className={`mt-2 text-[13px] leading-5 sm:mt-5 sm:text-sm sm:leading-7 ${
              isDark ? "text-white/55" : "text-slate-500"
            }`}>
              Kullanıcı adı ve şifreniz ile giriş yapın.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
            <div>
              <label className={`mb-1.5 block text-[13px] font-semibold sm:mb-2 sm:text-sm ${
                isDark ? "text-white/80" : "text-slate-700 font-bold"
              }`}>
                Kullanıcı Adı
              </label>

              <div className={`flex items-center gap-3 border-b pb-2.5 sm:pb-3 ${
                isDark ? "border-white/15" : "border-slate-300"
              }`}>
                <User size={18} className={isDark ? "text-white/55" : "text-slate-400"} />

                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Kullanıcı adınız"
                  className={`w-full bg-transparent outline-none ${
                    isDark ? "text-white placeholder:text-white/30" : "text-slate-900 placeholder:text-slate-400 font-medium"
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`mb-1.5 block text-[13px] font-semibold sm:mb-2 sm:text-sm ${
                isDark ? "text-white/80" : "text-slate-700 font-bold"
              }`}>
                Şifre
              </label>

              <div className={`flex items-center gap-3 border-b pb-2.5 sm:pb-3 ${
                isDark ? "border-white/15" : "border-slate-300"
              }`}>
                <Lock size={18} className={isDark ? "text-white/55" : "text-slate-400"} />

                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifreniz"
                  className={`w-full bg-transparent outline-none ${
                    isDark ? "text-white placeholder:text-white/30" : "text-slate-900 placeholder:text-slate-400 font-medium"
                  }`}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className={isDark ? "text-white/45 hover:text-white/80" : "text-slate-400 hover:text-slate-700"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setRememberMe((prev) => !prev)}
                className={`inline-flex items-center gap-2 text-[12px] sm:text-sm ${
                  isDark ? "text-white/70" : "text-slate-700 font-medium"
                }`}
              >
                <CheckSquare
                  size={16}
                  className={rememberMe ? "text-yellow-500 font-bold" : isDark ? "text-white/35" : "text-slate-400"}
                />
                Beni Hatırla
              </button>

              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className={`inline-flex items-center gap-1.5 text-[12px] font-bold transition sm:text-sm ${
                  isDark ? "text-yellow-300 hover:text-yellow-200" : "text-amber-600 hover:text-amber-700"
                }`}
              >
                <KeyRound size={15} />
                Şifremi Unuttum
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`mt-1 w-full rounded-2xl border px-5 py-3.5 text-lg font-black transition disabled:opacity-60 sm:mt-3 sm:rounded-full sm:py-4 sm:text-2xl ${
                isDark
                  ? "border-white/10 bg-black text-white hover:border-yellow-500/25 hover:bg-[#111]"
                  : "border-yellow-500/40 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-black shadow-lg hover:from-yellow-400 hover:to-amber-400"
              }`}
            >
              {loading ? "Giriş..." : "Giriş"}
            </button>
          </form>
        </div>
      </div>

      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm">
          <div className={`w-full max-w-[430px] rounded-[2rem] border p-7 shadow-2xl ${
            isDark ? "border-yellow-500/25 bg-[#111] text-white" : "border-slate-200 bg-white text-slate-900"
          }`}>
            <div className="mb-6">
              <div className={`text-xs font-bold uppercase tracking-[0.28em] ${isDark ? "text-yellow-300" : "text-amber-600"}`}>
                Şifre Talebi
              </div>

              <h2 className={`mt-2 text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                Şifremi Unuttum
              </h2>

              <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/50" : "text-slate-500"}`}>
                Kullanıcı adınızı ve telefon numaranızı girin. Talebiniz yöneticinize iletilecektir.
              </p>
            </div>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <input
                type="text"
                value={forgotUsername}
                onChange={(e) => setForgotUsername(e.target.value)}
                placeholder="Kullanıcı Adı"
                className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none focus:border-yellow-500 ${
                  isDark ? "border-yellow-500/20 bg-black/45 text-white placeholder:text-white/35" : "border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400"
                }`}
              />

              <input
                type="text"
                value={forgotPhone}
                onChange={(e) => setForgotPhone(e.target.value)}
                placeholder="Telefon Numarası"
                className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none focus:border-yellow-500 ${
                  isDark ? "border-yellow-500/20 bg-black/45 text-white placeholder:text-white/35" : "border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400"
                }`}
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotUsername("");
                    setForgotPhone("");
                  }}
                  className={`w-full rounded-2xl border px-5 py-3 font-bold transition ${
                    isDark ? "border-white/10 bg-black/40 text-white/65 hover:text-white" : "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  İptal
                </button>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full rounded-2xl bg-yellow-500 px-5 py-3 font-bold text-black transition hover:bg-yellow-400 disabled:opacity-60"
                >
                  {forgotLoading ? "Gönderiliyor..." : "Talep Gönder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {forcePasswordUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
          <div className="w-full max-w-[430px] rounded-[2rem] border border-yellow-500/25 bg-[#111] p-7 shadow-[0_24px_80px_rgba(0,0,0,0.65)]">
            <div className="mb-6">
              <div className="text-xs font-bold uppercase tracking-[0.28em] text-yellow-300">
                Güvenlik Kontrolü
              </div>

              <h2 className="mt-2 text-2xl font-black text-white">
                Şifrenizi Güncelleyin
              </h2>

              <p className="mt-2 text-sm leading-6 text-white/50">
                Sisteme devam edebilmek için size verilen geçici şifreyi değiştirmeniz gerekir.
              </p>
            </div>

            {forcePasswordError && (
              <div className="mb-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-200">
                {forcePasswordError}
              </div>
            )}

            <form onSubmit={handleForcePasswordChange} className="space-y-4">
              <input
                type="password"
                value={forcePasswordForm.currentPassword}
                onChange={(e) =>
                  setForcePasswordForm((prev) => ({
                    ...prev,
                    currentPassword: e.target.value,
                  }))
                }
                placeholder="Mevcut Şifre"
                className="h-12 w-full rounded-2xl border border-yellow-500/20 bg-black/45 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:border-yellow-400"
              />

              <input
                type="password"
                value={forcePasswordForm.newPassword}
                onChange={(e) =>
                  setForcePasswordForm((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
                placeholder="Yeni Şifre"
                className="h-12 w-full rounded-2xl border border-yellow-500/20 bg-black/45 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:border-yellow-400"
              />

              <input
                type="password"
                value={forcePasswordForm.confirmPassword}
                onChange={(e) =>
                  setForcePasswordForm((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                placeholder="Yeni Şifre Tekrar"
                className="h-12 w-full rounded-2xl border border-yellow-500/20 bg-black/45 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:border-yellow-400"
              />

              <button
                type="submit"
                disabled={forcePasswordSaving}
                className="w-full rounded-2xl bg-yellow-500 px-5 py-3 font-bold text-black transition hover:bg-yellow-400 disabled:opacity-60"
              >
                {forcePasswordSaving ? "Güncelleniyor..." : "Şifremi Güncelle"}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConsentModal
        user={consentUser}
        scrolled={consentScrolled}
        accepting={consentAccepting}
        error={consentError}
        onScroll={handleConsentScroll}
        onAccept={handleAcceptConsent}
      />
      <AgencyTrackingModal
        user={agencyTrackingUser}
        saving={agencyTrackingSaving}
        error={agencyTrackingError}
        onSelect={handleAgencyTrackingSelect}
      />
    </div>
  );
}
